import {
  Component,
  ViewEncapsulation,
  signal,
  computed,
  OnDestroy,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ButtonComponent } from '../../atoms/button/button';
import { NavigationService } from '../../../core/services/navigation.service';
import { NavigationNavItem } from '../../../core/models/navigation.models';

@Component({
  selector: 'ds-header',
  templateUrl: './header.html',
  styleUrl: './header.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent],
})
export class HeaderComponent implements OnDestroy {
  private readonly doc = inject(DOCUMENT);
  private readonly navigationService = inject(NavigationService);

  readonly navigation = this.navigationService.navigation;
  readonly navItems = computed<NavigationNavItem[]>(() => this.navigation()?.headerNav ?? []);
  readonly activeMenu = signal<string | null>(null);
  readonly mobileOpen = signal(false);
  readonly expandedMobileSections = signal(new Set<string>());

  readonly hasActiveMenu = computed(() => this.activeMenu() !== null);

  private openTimeout: ReturnType<typeof setTimeout> | null = null;
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;

  toggleMenu(label: string): void {
    this.clearTimeouts();
    this.activeMenu.update(curr => (curr === label ? null : label));
  }

  openMenu(label: string): void {
    this.clearTimeouts();
    this.openTimeout = setTimeout(() => {
      this.activeMenu.set(label);
    }, 150);
  }

  closeMenu(): void {
    this.clearTimeouts();
    this.closeTimeout = setTimeout(() => {
      this.activeMenu.set(null);
    }, 300);
  }

  cancelClose(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    this.doc.documentElement.style.overflow = this.mobileOpen() ? 'hidden' : '';
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
    this.doc.documentElement.style.overflow = '';
  }

  toggleMobileSection(label: string): void {
    this.expandedMobileSections.update(set => {
      const next = new Set(set);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  isMobileSectionExpanded(label: string): boolean {
    return this.expandedMobileSections().has(label);
  }

  /**
   * Close the active desktop mega-panel when a link inside it is clicked.
   * Runs synchronously so the panel is closed BEFORE the browser handles
   * the navigation (or, for modifier-clicks / target=_blank, BEFORE control
   * returns to the user with the menu still draped over the page).
   *
   * Restores focus to the originating nav-trigger button if focus was inside
   * the mega-panel — prevents orphan focus when the panel is removed.
   */
  handleMegaLinkClick(label: string): void {
    this.clearTimeouts();
    this.activeMenu.set(null);
    // Restore focus to the trigger ONLY if the active element is inside the
    // mega-panel that was just closed. For modifier-clicks the browser keeps
    // the link as activeElement; we don't want to steal focus from a new tab
    // flow if the user is mid-keyboard-navigation. The querySelector is scoped
    // to the host element implicitly via getElementById on the unique id we
    // already render (`mega-{label}`).
    const panel = this.doc.getElementById('mega-' + label);
    if (panel && panel.contains(this.doc.activeElement)) {
      const trigger = this.doc.querySelector<HTMLButtonElement>(
        `button[aria-controls="mega-${label}"]`
      );
      trigger?.focus();
    }
  }

  /**
   * Close the mobile drawer when a link inside it is clicked.
   * Reuses `closeMobile()` so the body scroll lock is also released
   * (`document.documentElement.style.overflow = ''`).
   *
   * Also collapses any expanded mobile sections so reopening the drawer
   * gives a clean state.
   */
  handleMobileLinkClick(): void {
    this.closeMobile();
    this.expandedMobileSections.set(new Set<string>());
  }

  onKeydown(event: KeyboardEvent, label: string): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.activeMenu() === label) {
          this.activeMenu.set(null);
        } else {
          this.activeMenu.set(label);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.activeMenu.set(null);
        (event.target as HTMLElement).focus();
        break;
    }
  }

  isExternal(href: string): boolean {
    return href.startsWith('http');
  }

  hasDropdown(item: NavigationNavItem): boolean {
    return (item.links?.length ?? 0) > 0 || (item.groups?.length ?? 0) > 0;
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.doc.documentElement.style.overflow = '';
  }

  private clearTimeouts(): void {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }
}
